import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

export default function ServiceJobsReportSection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const { data, isLoading, error } = useQuery({
    queryKey: ['serviceJobsReport', from_date, to_date],
    queryFn: async () =>
      reportsApi.getServiceJobsReport({ from_date, to_date }) as Promise<{
        summary: {
          total_jobs: number;
          incomplete: number;
          total_amount: number;
          completed_amount: number;
          by_status: Record<string, number>;
        };
        by_staff: Array<{
          assigned_to_name: string;
          jobs: number;
          completed: number;
          incomplete: number;
          amount: number;
        }>;
        jobs: Array<Record<string, unknown>>;
      }>,
  });

  const handleExport = () => {
    if (!data?.jobs?.length) return;
    downloadCsv(
      'service-jobs-report.csv',
      [
        { key: 'service', label: 'Service' },
        { key: 'customer', label: 'Customer' },
        { key: 'staff', label: 'Staff' },
        { key: 'status', label: 'Status' },
        { key: 'amount', label: 'Amount' },
        { key: 'scheduled', label: 'Scheduled' },
        { key: 'invoice', label: 'Invoice' },
      ],
      data.jobs.map((row) => ({
        service: row.item_name,
        customer: row.customer_name,
        staff: row.assigned_to_name || 'Unassigned',
        status: row.status,
        amount: Number(row.amount || 0).toFixed(2),
        scheduled: row.scheduled_at || '',
        invoice: row.pos_invoice || '',
      })),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ReportDateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
        />
        <ReportExportButton onClick={handleExport} disabled={!data?.jobs?.length} />
      </div>

      {isLoading && <p className="text-sm text-gray-500">{t('common.loading', 'Loading...')}</p>}
      {error && <p className="text-sm text-red-600">{t('common.error', 'Failed to load')}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl bg-white border border-gray-100 p-3">
              <p className="text-xs text-gray-500">{t('services.totalJobs', 'Total jobs')}</p>
              <p className="text-xl font-bold">{data.summary.total_jobs}</p>
            </div>
            <div className="rounded-xl bg-white border border-gray-100 p-3">
              <p className="text-xs text-gray-500">{t('services.incomplete', 'Incomplete')}</p>
              <p className="text-xl font-bold text-amber-700">{data.summary.incomplete}</p>
            </div>
            <div className="rounded-xl bg-white border border-gray-100 p-3">
              <p className="text-xs text-gray-500">{t('services.completedAmount', 'Completed amount')}</p>
              <p className="text-xl font-bold text-emerald-700">{formatMoney(data.summary.completed_amount)}</p>
            </div>
            <div className="rounded-xl bg-white border border-gray-100 p-3">
              <p className="text-xs text-gray-500">{t('services.totalAmount', 'Total amount')}</p>
              <p className="text-xl font-bold">{formatMoney(data.summary.total_amount)}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2">{t('services.staff', 'Staff')}</th>
                  <th className="px-3 py-2">{t('services.jobs', 'Jobs')}</th>
                  <th className="px-3 py-2">{t('services.completed', 'Completed')}</th>
                  <th className="px-3 py-2">{t('services.incomplete', 'Incomplete')}</th>
                  <th className="px-3 py-2">{t('common.amount', 'Amount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.by_staff.map((row) => (
                  <tr key={row.assigned_to_name} className="border-t border-gray-50">
                    <td className="px-3 py-2 font-medium">{row.assigned_to_name}</td>
                    <td className="px-3 py-2">{row.jobs}</td>
                    <td className="px-3 py-2">{row.completed}</td>
                    <td className="px-3 py-2 text-amber-700">{row.incomplete}</td>
                    <td className="px-3 py-2 font-semibold">{formatMoney(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
